<template>
  <div :style="menuStyleVars" class="menu-wide">
    <!-- Logo section - hidden when platform navigation is enabled (logo is in top bar) -->
    <VcLayout v-if="showLogo" @click="redirectToHome" class="logo" column>
      <VcSkeleton v-if="isMenuLoading" type="button" width="100%"></VcSkeleton>
      <VcImage :image="logo" v-else-if="isLogo"/>
    </VcLayout>

    <VcLayout v-if="displayQuickActions" align-start class="quick-actions-container">
      <VcSkeleton v-if="isMenuLoading" class="quick-actions" type="chip" min-height="40" width="155px"></VcSkeleton>
      <QuickActions :is-actions-disabled="isActionsDisabled" v-else class="quick-actions"/>
    </VcLayout>

    <div class="menu-items-group">
      <VcMenuItem v-for="(menuItem, index) in (isMenuLoading ? skeletonMenuItems : mappedMenuItems)"
                  v-bind="menuItem"
                  flavor="desktop"
                  :lightBackground="hasPlatformNavigation"
                  class="menu-item"
                  size="md"
                  :key="index"
                  :data-qa="`VcMenuItem-${menuItem.name}`"
                  :is-loading="isMenuLoading || menuItem.isLoading"
                  :isSelected="isSelected(menuItem)"
                  :selected-subitem="selectedSubItemIndex(menuItem)"
                  :is-expanded="isMenuItemExpanded(menuItem)"
                  :label="menuItem.label"
                  @click="onMenuItemClick(menuItem)"
                  @onSubmenuClicked="subItem => onMenuItemClick(subItem)"
                  @change="expanded => onExpandMenuItem(expanded, menuItem)">
        <template #actions v-if="menuItem.rightIcon">
            <VcButton small icon @click="onRightAreaClick(menuItem)">
              <template #prepend v-if="menuItem.rightIcon">
                <VcIcon class="right-icon" size="16" :color="hasPlatformNavigation ? 'black' : textColor"
                        :data-qa="`right-area-vc-icon-${menuItem.name}`">{{ menuItem.rightIcon }}</VcIcon>
              </template>
            </VcButton>
        </template>
        <template #actions v-else-if="menuItem.name === 'checklist'">
            <VcProgressCircular v-if="checklistProgress.show"
              size="sm"
              :currentStep="checklistProgress.currentStep"
              :steps="checklistProgress.totalSteps"
              :fontColorByTheme="!hasPlatformNavigation"
            />
        </template>
        <template #actions v-else-if="menuItem.name === 'inbox'">
          <VcBadge v-if="conversationCounts"
                   inline
                   flavor="neutral"
                   data-qa="inbox-counter"
                   :badgeText="conversationCounts" />
        </template>
      </VcMenuItem>
    </div>
    <PoweredByContainer v-if="!hasPlatformNavigation" class="powered-by-container" :text-color="textColor"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import QuickActions from '@/modules/layout/quickActions/components/quickActions.vue';
import { getItemNavigationUrl } from '@/modules/layout/platformNavigation/utils/navigationUtils';
import PoweredByContainer from './PoweredByContainer.vue';

export default {
  name: 'MainMenuWide',
  components: { QuickActions, PoweredByContainer },
  data() {
    return {
      selectedItem: null,
      expandedMenuItems: [],
      queuedMenuItem: null,
      appliedExpandSignature: null,
    };
  },
  props: {
    mappedMenuItems: {
      type: Array,
      default: () => [],
    },
    checklistProgress: {
      type: Object,
      default: () => ({
        show: false,
        currentStep: 0,
        totalSteps: 0,
      }),
    },
    conversationCounts: {
      type: Number,
      default: 0,
    },
    isMenuLoading: {
      type: Boolean,
      default: true,
    },
    isActionsDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to show the logo in the sidebar
     * Set to false when platform navigation is enabled (logo is in top bar)
     */
    showLogo: {
      type: Boolean,
      default: true,
    },
    removeQuickAction: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.$router.beforeEach(this.routeChange);
  },
  watch: {
    mappedMenuItems: {
      immediate: true,
      handler() {
        this.applyExpandDefaults();
        this.routeChange();
      },
    },
    isAngularLoaded() {
      if (this.isAngularLoaded && this.queuedMenuItem) {
        this.selectMenuItem(this.queuedMenuItem);
      }
    },
  },
  computed: {
    ...mapGetters('BusinessStore', ['logo', 'primaryColor', 'textColor']),
    ...mapGetters('AngularStore', ['isAngularLoaded']),
    isLogo() {
      return this.logo;
    },
    hasPlatformNavigation() {
      return this.$hasFeature('rollout.platform_administration.platform_navigation');
    },
    menuStyleVars() {
      if (this.hasPlatformNavigation) {
        return {
          'background-color': 'white',
          color: 'black',
        };
      }
      return {
        'background-color': this.primaryColor,
      };
    },
    skeletonMenuItems() {
      return Array.from(Array(10)).map(() => ({
        label: 'Label 1',
        isLoading: true,
        size: 'md',
        submenuItems: [],
        flavor: 'desktop',
      }));
    },
    displayQuickActions() {
      return this.$hasFeature('pkg.business_administration.quick_actions') && !this.removeQuickAction;
    },
  },
  methods: {
    getItemPath(item) {
      if (!item) return null;
      const path = getItemNavigationUrl(item);
      return path?.split('?')[0] || null;
    },
    isSelected(item) {
      // Platform navigation items carry an isActive flag computed from the store.
      // Use it directly so selection is immediate (no async route-change delay).
      if (item.isActive !== undefined) {
        return item.isActive || this.hasSelectedSubItem(item);
      }
      if (!this.selectedItem) return false;
      const selectedPath = this.getItemPath(this.selectedItem);
      const itemPath = this.getItemPath(item);
      return (
        (selectedPath && itemPath && selectedPath.includes(itemPath))
        || (this.selectedItem.entity_path && this.selectedItem.entity_path === item.entity_path)
        || this.hasSelectedSubItem(item)
      );
    },
    redirectToHome() {
      this.$router.push({ name: 'dashboard' });
    },
    onMenuItemClick(item) {
      if (this.isAngularLoaded || item.type === 'pov') {
        this.selectMenuItem(item);
        this.queuedMenuItem = null;
      } else {
        this.queuedMenuItem = item;
      }
    },
    selectMenuItem(item) {
      const isInternalAction = item.action?.name === 'navigate' && item.action?.payload?.newTab !== true;
      if ((!item.action || isInternalAction) && !item.dispatchAction) this.selectedItem = item;
      this.$emit('menuItemClick', item);
    },
    onRightAreaClick(item) {
      this.$emit('rightAreaClick', item);
    },
    selectedSubItemIndex(menuItem) {
      // Platform navigation items carry isActive on each sub-item — use it directly
      if (menuItem.submenuItems?.length && menuItem.submenuItems[0]?.isActive !== undefined) {
        return menuItem.submenuItems.findIndex((item) => item.isActive);
      }
      const selectedPath = this.getItemPath(this.selectedItem);
      return menuItem.submenuItems?.findIndex((item) => {
        const itemPath = this.getItemPath(item);
        return (selectedPath && itemPath && selectedPath === itemPath)
          || (item.entity_path && item.entity_path === this.selectedItem?.entity_path);
      });
    },
    hasSelectedSubItem(menuItem) {
      return this.selectedSubItemIndex(menuItem) !== -1;
    },
    applyExpandDefaults() {
      if (!this.mappedMenuItems?.length) return;
      const signature = this.mappedMenuItems.map((item) => item.name).join('|');
      if (signature === this.appliedExpandSignature) return;
      this.appliedExpandSignature = signature;
      this.expandedMenuItems = [];

      this.mappedMenuItems.forEach((item) => {
        if (item.expandByDefault && item.submenuItems?.length) {
          this.expandedMenuItems.push(item);
        }
      });
    },
    onExpandMenuItem(expanded, item) {
      if (expanded) {
        this.onMenuItemClick(item.submenuItems[0]);
        this.expandedMenuItems.push(item);
      } else {
        this.expandedMenuItems = this.expandedMenuItems.filter((menuItem) => menuItem.name !== item.name);
      }
    },
    isMenuItemExpanded(item) {
      const elementFound = this.expandedMenuItems.find((menuItem) => menuItem.name === item.name);
      return !!elementFound;
    },
    routeChange(to, from, next = () => {}) {
      let pagePath = window.location.pathname + window.location.search; // in case of created state
      if (to?.path) {
        pagePath = `/app${to.path}`;
        if (to.query && to.query.length) {
          pagePath += `?${new URLSearchParams(to.query).toString()}`;
        }
      }
      this.mappedMenuItems.forEach((item) => {
        if (this.samePath(pagePath, item)) {
          this.selectedItem = item;
          if (item.submenuItems?.length && !this.isMenuItemExpanded(item)) {
            this.expandedMenuItems.push(item);
          }
        } else if (item.submenuItems.length) {
          item.submenuItems.forEach((subItem) => {
            if (this.samePath(pagePath, subItem)) {
              this.selectedItem = subItem;
              if (!this.isMenuItemExpanded(item)) {
                this.expandedMenuItems.push(item);
              }
            }
          });
        }
      });
      next();
    },
    samePath(pagePath, item) {
      const itemPath = this.getItemPath(item);
      return (itemPath && pagePath.includes(itemPath)) || item.entity_path?.some((path) => pagePath.includes(path));
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-wide{
  position: relative;
  width: 240px;
  overflow-y: hidden;
  height: 100%;
  text-align: start;
  display: flex;
  flex-direction: column;
  border-inline-end: 1px solid var(--gray-lighten-2);

  .quick-actions-container {
    flex: 0 0 var(--size-value15);
  }

  .powered-by-container {
    flex: 0 0 var(--size-value8);
    margin: 0;
  }
}

.logo {
  flex: 0 0 var(--size-value10);
  height: var(--size-value10);
  width: 174px;
  margin-inline-start: var(--size-value4);
  margin-top: var(--size-value4);
  cursor: pointer;

  &::v-deep {
    .VcImage {
      justify-content: start;

      .image {
       overflow: hidden;
      }
    }
  }
}

.quick-actions{
  margin-inline-start: var(--size-value4);
  margin-top: var(--size-value5);
}

.menu-items-group{
  margin-top: var(--size-value5);
  overflow-y: auto;
  flex: 1 1 0;

  ::v-deep {
    .right-icon {
      opacity: 0.72;
    }
  }
}

.menu-items-group::-webkit-scrollbar {
  width: 6px;
}
.menu-items-group::-webkit-scrollbar-thumb {
  background-color: var(--gray-darken-1);
  border-radius: 10px;
}
</style>
